import { createContext, useContext, useEffect, useState } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { auth, db } from "../firebase";
import { checkRateLimit, resetRateLimit } from "../utils/rateLimit";

const StoreContext = createContext();

export const StoreProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasedCourseIds, setPurchasedCourseIds] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [hasFreeAudit, setHasFreeAudit] = useState(false);
  const [hasSoftware, setHasSoftware] = useState(false);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState("");
  const [cart, setCart] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        const userDocRef = doc(db, "users", firebaseUser.uid);
        try {
          const userDoc = await getDoc(userDocRef);
          if (!userDoc.exists()) {
            // crear documento inicial si no existe
            await setDoc(userDocRef, {
              email: firebaseUser.email,
              role: "student",
              purchasedCourses: [],
              hasFreeAudit: false,
              hasSoftware: false,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
            setPurchasedCourseIds([]);
            setUserRole("student");
            setHasFreeAudit(false);
            setHasSoftware(false);
            setUserData({ role: "student", hasFreeAudit: false, hasSoftware: false });
          } else {
            const data = userDoc.data();
            setPurchasedCourseIds(data.purchasedCourses || []);
            setUserRole(data.role || "student");
            setHasFreeAudit(data.hasFreeAudit || false);
            setHasSoftware(data.hasSoftware || false);
            setUserData(data);
          }
        } catch (err) {
          console.error("Error cargando/creando doc de usuario:", err);
        }
      } else {
        setUser(null);
        setPurchasedCourseIds([]);
        setUserRole(null);
        setHasFreeAudit(false);
        setHasSoftware(false);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const sanitizeInput = (input) => {
    return input.trim().replace(/[<>\"'&]/g, "");
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    if (password.length < 6) return "La contraseña debe tener al menos 6 caracteres";
    if (!/[A-Z]/.test(password)) return "La contraseña debe tener al menos una mayúscula";
    if (!/[0-9]/.test(password)) return "La contraseña debe tener al menos un número";
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return "La contraseña debe tener al menos un símbolo especial (!@#$%...)";
    return null;
  };

  const signup = async ({ email, password }) => {
    try {
      setError("");

      const { allowed, remainingMs } = checkRateLimit("signup", 3, 60000);
      if (!allowed) {
        const secs = Math.ceil(remainingMs / 1000);
        setError(`Demasiados intentos de registro. Espera ${secs}s.`);
        return false;
      }

      const sanitizedEmail = sanitizeInput(email);
      // NO sanitizar password: los símbolos especiales son requeridos
      const cleanPassword = password.trim();

      if (!validateEmail(sanitizedEmail)) {
        setError("Email inválido");
        return false;
      }

      const passwordError = validatePassword(cleanPassword);
      if (passwordError) {
        setError(passwordError);
        return false;
      }

      // Crear usuario en Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        sanitizedEmail,
        cleanPassword
      );

      const firebaseUser = userCredential.user;

      // Guardar datos del usuario en Firestore
      try {
        await setDoc(doc(db, "users", firebaseUser.uid), {
          email: firebaseUser.email,
          role: "student",
          purchasedCourses: [],
          hasFreeAudit: false,
          hasSoftware: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      } catch (firestoreErr) {
        console.error("Error guardando en Firestore:", firestoreErr);
        // El usuario se creó en Authentication, continuamos
      }

      setUser(firebaseUser);
      setPurchasedCourseIds([]);
      setHasFreeAudit(false);
      setHasSoftware(false);
      setUserData({ role: "student", hasFreeAudit: false, hasSoftware: false });
      resetRateLimit("signup");
      return true;
    } catch (error) {
      console.error("Error en signup:", error.message);
      if (error.code === "auth/email-already-in-use") {
        setError("Este email ya está registrado");
      } else if (error.code === "auth/invalid-email") {
        setError("Email inválido");
      } else if (error.code === "auth/weak-password") {
        setError("La contraseña es muy débil");
      } else if (error.code === "auth/operation-not-allowed") {
        setError("Operación no permitida. Contacta al soporte.");
      } else {
        setError("Error al crear la cuenta. Intenta más tarde.");
      }
      return false;
    }
  };

  const login = async ({ email, password }) => {
    try {
      setError("");

      const { allowed, remainingMs } = checkRateLimit("login", 5, 60000);
      if (!allowed) {
        const secs = Math.ceil(remainingMs / 1000);
        setError(`Demasiados intentos. Espera ${secs}s.`);
        return false;
      }

      const sanitizedEmail = sanitizeInput(email);
      // NO sanitizar password: los símbolos deben llegar intactos a Firebase
      const cleanPassword = password.trim();

      if (!validateEmail(sanitizedEmail)) {
        setError("Email inválido");
        return false;
      }

      await signInWithEmailAndPassword(auth, sanitizedEmail, cleanPassword);
      resetRateLimit("login");
      return true;
    } catch (error) {
      console.error("Error en login:", error.message);
      if (error.code === "auth/user-not-found") {
        setError("Usuario no encontrado");
      } else if (error.code === "auth/wrong-password") {
        setError("Contraseña incorrecta");
      } else if (error.code === "auth/invalid-email") {
        setError("Email inválido");
      } else if (error.code === "auth/too-many-requests") {
        setError("Demasiados intentos. Intenta más tarde.");
      } else {
        setError("Credenciales incorrectas. Verifica tu email y contraseña.");
      }
      return false;
    }
  };

  const logout = async () => {
    try {
      setError("");
      await signOut(auth);
      setUser(null);
      setPurchasedCourseIds([]);
      setHasFreeAudit(false);
      setHasSoftware(false);
      setUserData(null);
    } catch (error) {
      console.error("Error en logout:", error);
      setError("Error al cerrar sesión");
    }
  };

  const purchaseCourse = async (courseId) => {
    if (!user) {
      setError("Debes iniciar sesión para comprar");
      return false;
    }

    try {
      const userDocRef = doc(db, "users", user.uid);
      // setDoc con merge crea el documento si falta
      await setDoc(
        userDocRef,
        {
          purchasedCourses: arrayUnion(courseId),
          updatedAt: new Date(),
        },
        { merge: true }
      );

      setPurchasedCourseIds((prev) => [...prev, courseId]);
      return true;
    } catch (err) {
      console.error("Error al comprar curso:", err);
      setError("Error al comprar curso");
      return false;
    }
  };

  const addToCart = (item) => {
    setCart((prev) => {
      // evitar duplicados
      if (prev.find((c) => c.id === item.id)) return prev;
      return [...prev, item];
    });
  };

  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((c) => c.id !== id));
  };

  const clearCart = () => setCart([]);

  const activateFreeAudit = async (info = {}) => {
    if (!user) {
      setError("Debes iniciar sesión para activar tu auditoría gratis");
      return false;
    }

    try {
      const userDocRef = doc(db, "users", user.uid);
      await setDoc(
        userDocRef,
        {
          hasFreeAudit: true,
          auditInfo: info,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      setHasFreeAudit(true);
      return true;
    } catch (err) {
      console.error("Error activando auditoría:", err);
      setError("Error al activar la auditoría gratis");
      return false;
    }
  };

  const value = {
    user,
    loading,
    error,
    setError,
    purchasedCourseIds,
    userRole,
    userData,
    hasFreeAudit,
    hasSoftware,
    cart,
    addToCart,
    removeFromCart,
    clearCart,
    signup,
    login,
    logout,
    purchaseCourse,
    activateFreeAudit,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore debe usarse dentro de StoreProvider");
  }
  return context;
};

